import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BookOpen, Plus, Edit, Trash2, Calendar, Sparkles, Loader2, PenLine, ChevronRight, Search, X, Lightbulb, Target, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Reflection: "bg-sky-100 text-sky-700",
  Gratitude: "bg-amber-100 text-amber-700",
  "Goal Check-in": "bg-emerald-100 text-emerald-700",
  "Emotional Awareness": "bg-purple-100 text-purple-700",
  Growth: "bg-rose-100 text-rose-700",
  "Free Write": "bg-gray-100 text-gray-700",
};

export default function PerformanceJournal({ user, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWrite, setShowWrite] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // AI insights & goal suggestions
  const [activeInsights, setActiveInsights] = useState(null); // { entryId, insights, loading }
  const [goalSuggestions, setGoalSuggestions] = useState(null); // { entryId, suggestions, loading }
  const [creatingGoal, setCreatingGoal] = useState(null);

  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Free Write");

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/journal/entries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(res.data);
    } catch (err) {
      console.error("Failed to load entries:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/journal/ai-prompts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrompts(res.data);
    } catch (err) {
      toast.error("Failed to load prompts");
    } finally {
      setPromptsLoading(false);
    }
  };

  const openFreeWrite = () => {
    setTitle("");
    setContent("");
    setCategory("Free Write");
    setSelectedPrompt(null);
    setEditingEntry(null);
    setShowWrite(true);
  };

  const openWithPrompt = (prompt) => {
    setTitle(prompt.category);
    setContent("");
    setCategory(prompt.category);
    setSelectedPrompt(prompt);
    setEditingEntry(null);
    setShowWrite(true);
  };

  const openEdit = (entry) => {
    setTitle(entry.title || "");
    setContent(entry.goals_for_day || entry.performance_highlights || "");
    setCategory(entry.tags?.[0] || "Free Write");
    setSelectedPrompt(null);
    setEditingEntry(entry);
    setShowWrite(true);
  };

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error("Write something before saving");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const payload = {
        title: title || category || "Journal Entry",
        date: new Date().toISOString().split("T")[0],
        goals_for_day: content,
        tags: [category],
      };

      if (editingEntry) {
        await axios.put(`${API}/journal/entries/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/journal/entries`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Entry saved!");
      }
      setShowWrite(false);
      fetchEntries();
    } catch (err) {
      toast.error("Failed to save entry");
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/journal/entries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Entry deleted");
      fetchEntries();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const getAIInsights = async (entryId) => {
    setActiveInsights({ entryId, insights: null, loading: true });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/journal/entries/${entryId}/ai-insights`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveInsights({ entryId, insights: res.data.insights, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to get insights");
      setActiveInsights(null);
    }
  };

  const suggestGoals = async (entryId) => {
    setGoalSuggestions({ entryId, suggestions: null, loading: true });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/journal/entries/${entryId}/suggest-goals`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGoalSuggestions({ entryId, suggestions: res.data.suggestions, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to suggest goals");
      setGoalSuggestions(null);
    }
  };

  const createGoalFromSuggestion = async (goal) => {
    setCreatingGoal(goal.title);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/journal/create-goal-from-journal`, goal, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Goal created: ${goal.title}`);
      setGoalSuggestions((prev) => prev ? {
        ...prev,
        suggestions: prev.suggestions.filter((g) => g.title !== goal.title),
      } : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create goal");
    } finally {
      setCreatingGoal(null);
    }
  };

  const filtered = entries.filter((e) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (e.title || "").toLowerCase().includes(q) ||
      (e.goals_for_day || "").toLowerCase().includes(q) ||
      (e.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch (err) {
      return d;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-10">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">Journal</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Free-write or use AI prompts to reflect on your mental game</p>
          </div>
          <Button data-testid="new-entry-btn" onClick={openFreeWrite} className="bg-teal-600 hover:bg-teal-700">
            <PenLine className="w-4 h-4 mr-1.5" /> Write
          </Button>
        </div>

        {/* AI Prompts Section */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 mb-6 border border-indigo-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h3 className="font-bold text-gray-900 text-base">AI Suggested Prompts</h3>
            </div>
            <Button
              data-testid="load-prompts-btn"
              size="sm"
              onClick={fetchPrompts}
              disabled={promptsLoading}
              className="bg-indigo-600 hover:bg-indigo-700 h-8"
            >
              {promptsLoading ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Loading...</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5 mr-1" /> {prompts.length ? "Refresh" : "Get Prompts"}</>
              )}
            </Button>
          </div>
          {prompts.length > 0 ? (
            <div className="space-y-2">
              {prompts.map((p, i) => (
                <button
                  key={i}
                  data-testid={`prompt-${i}`}
                  onClick={() => openWithPrompt(p)}
                  className="w-full text-left flex items-center justify-between bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-all group"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[p.category] || CATEGORY_COLORS["Free Write"]}`}>
                      {p.category}
                    </span>
                    <span className="text-sm text-gray-700">{p.prompt}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 ml-2 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Tap &ldquo;Get Prompts&rdquo; for personalized journal prompts based on your goals and recent check-ins.</p>
          )}
        </div>

        {/* Search */}
        {entries.length > 0 && (
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="journal-search"
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white/80 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>
        )}

        {/* Entries List */}
        <div className="space-y-3">
          {filtered.map((entry) => {
            const cat = entry.tags?.[0] || "Free Write";
            const text = entry.goals_for_day || entry.performance_highlights || "";
            return (
              <Card key={entry.id} data-testid={`entry-${entry.id}`} className="border-0 shadow-sm bg-white/90 hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-gray-900 text-base">{entry.title || "Untitled"}</h3>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[cat] || CATEGORY_COLORS["Free Write"]}`}>
                          {cat}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(entry.date)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button onClick={() => openEdit(entry)} className="p-1.5 text-gray-400 hover:text-gray-600">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(entry.id)} className="p-1.5 text-gray-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap line-clamp-4">
                    {text}
                  </p>
                  {/* AI Actions */}
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100 dark:border-gray-800">
                    <button
                      data-testid={`ai-insights-btn-${entry.id}`}
                      onClick={() => activeInsights?.entryId === entry.id ? setActiveInsights(null) : getAIInsights(entry.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        activeInsights?.entryId === entry.id 
                          ? "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" 
                          : "text-gray-500 hover:bg-purple-50 hover:text-purple-600 dark:text-gray-400 dark:hover:bg-purple-900/30"
                      }`}
                    >
                      {activeInsights?.entryId === entry.id && activeInsights.loading 
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Lightbulb className="w-3.5 h-3.5" />}
                      AI Insights
                    </button>
                    <button
                      data-testid={`suggest-goals-btn-${entry.id}`}
                      onClick={() => goalSuggestions?.entryId === entry.id ? setGoalSuggestions(null) : suggestGoals(entry.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        goalSuggestions?.entryId === entry.id 
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" 
                          : "text-gray-500 hover:bg-emerald-50 hover:text-emerald-600 dark:text-gray-400 dark:hover:bg-emerald-900/30"
                      }`}
                    >
                      {goalSuggestions?.entryId === entry.id && goalSuggestions.loading 
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Target className="w-3.5 h-3.5" />}
                      Suggest Goals
                    </button>
                  </div>

                  {/* AI Insights Panel */}
                  {activeInsights?.entryId === entry.id && activeInsights.insights && (
                    <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Lightbulb className="w-4 h-4 text-purple-600" />
                        <span className="text-xs font-bold text-purple-700 dark:text-purple-300">AI Insights</span>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                        {activeInsights.insights}
                      </div>
                    </div>
                  )}

                  {/* Goal Suggestions Panel */}
                  {goalSuggestions?.entryId === entry.id && goalSuggestions.suggestions && (
                    <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Target className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">Suggested Goals</span>
                      </div>
                      <div className="space-y-2">
                        {goalSuggestions.suggestions.map((goal, i) => (
                          <div key={i} className="flex items-start gap-2 bg-white dark:bg-gray-800 rounded-lg p-2.5">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">{goal.title}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{goal.description}</p>
                            </div>
                            <Button
                              data-testid={`create-goal-${i}`}
                              size="sm"
                              onClick={() => createGoalFromSuggestion(goal)}
                              disabled={creatingGoal === goal.title}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-2.5 flex-shrink-0"
                            >
                              {creatingGoal === goal.title ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Add</>}
                            </Button>
                          </div>
                        ))}
                        {goalSuggestions.suggestions.length === 0 && (
                          <p className="text-xs text-gray-500">All suggested goals have been created!</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {entries.length === 0 && !loading && (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No journal entries yet</h3>
            <p className="text-gray-500 mb-4 max-w-md mx-auto">Start writing to reflect on your mental performance, or use AI prompts for inspiration.</p>
            <div className="flex justify-center gap-3">
              <Button data-testid="empty-write-btn" onClick={openFreeWrite} className="bg-teal-600 hover:bg-teal-700">
                <PenLine className="w-4 h-4 mr-1.5" /> Free Write
              </Button>
              <Button data-testid="empty-prompts-btn" onClick={fetchPrompts} variant="outline" className="border-indigo-300 text-indigo-700">
                <Sparkles className="w-4 h-4 mr-1.5" /> Get Prompts
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Write / Edit Dialog */}
      <Dialog open={showWrite} onOpenChange={setShowWrite}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? "Edit Entry" : "New Journal Entry"}</DialogTitle>
          </DialogHeader>

          {selectedPrompt && (
            <div className={`rounded-lg p-3 mb-2 ${CATEGORY_COLORS[selectedPrompt.category] || "bg-gray-100"}`}>
              <p className="text-sm font-medium">{selectedPrompt.prompt}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <input
                data-testid="entry-title-input"
                type="text"
                placeholder="Title (optional)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
            <div>
              <Textarea
                data-testid="entry-content-input"
                placeholder={selectedPrompt ? "Write your response..." : "What's on your mind? Write freely..."}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={10}
                className="text-sm resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{content.length} characters</p>
            </div>
            <div className="flex space-x-3">
              <Button data-testid="save-entry-btn" onClick={handleSave} className="flex-1 bg-teal-600 hover:bg-teal-700">
                {editingEntry ? "Update" : "Save Entry"}
              </Button>
              <Button onClick={() => setShowWrite(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
